import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Text, Separator, Button } from '@cuemath/comps';
import { dateObjToEpoch, epochToDate } from 'utils';

import { EVENT_TYPE } from 'constants/logger';
import { trackEvent } from 'modules/analytics';
import {
  post as createStudent,
  invalidateList as invalidateStudentList,
} from 'actions/student-list';
import { get as getStudentSlots } from 'actions/student-slots';
import { patch as editStudent } from 'actions/students';

import {
  Head,
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  NavigationButtons,
  NavButton,
  renderFormField,
  getOptions,
} from 'modules/form';

import {
  FORM_ACTIONS,
  FIELD_TYPES,
  USER_TAB,
  studentFields,
  getOnChangeMethod,
  getInputChangeMethod,
  getHeadText,
  STUDENT_TYPE,
  USERNAME_LENGTH,
  OPTIONS,
} from 'constants/form';
import { PII_HIDDEN_FORM_ACTIONS } from '../../constants/form';

//Hiding due to PII changes
const HIDDEN_FIELDS = ['username', 'phone', 'alternatePhone'];

class CreateStudent extends PureComponent {
  static defaultProps = {
    student: null,
  };

  constructor(props) {
    super(props);
    const { student, formType } = this.props;
    this.state = {
      errorText: null,
      message: null,
      studentPost: store.post(),
      studentPatch: store.patch(),
      studentData: student,
      tab: USER_TAB.ACCOUNT,
      type: formType,
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
      updateStudent: false,
    };
    studentFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static propTypes = {
    actions: PropTypes.shape({
      createStudent: PropTypes.func.isRequired,
      editStudent: PropTypes.func.isRequired,
      invalidateStudentList: PropTypes.func.isRequired,
      getStudentSlots: PropTypes.func.isRequired,
    }).isRequired,
    student: PropTypes.object,
    onNavigate: PropTypes.func.isRequired,
    userConfig: PropTypes.object.isRequired,
    programs: PropTypes.array.isRequired,
  };

  getFormDefaults = () => {
    const { student } = this.props;
    const formDefaults = {};

    studentFields.forEach(field => {
      formDefaults[field.name] =
        !!student && student[field.param] ? student[field.param] : field.default;
      if (
        field.name === 'classEndDate' ||
        field.name === 'classStartDate' ||
        field.name === 'churnDate'
      ) {
        formDefaults[field.name] = student[field.param]
          ? epochToDate(student[field.param], 'YYYY-MM-DD')
          : field.default;
      }
      if (field.name === 'isTeacherTraining') {
        formDefaults[field.name] =
          student[field.param] === STUDENT_TYPE.TEACHER ? true : false;
      }
    });
    if (!student['username'])
      if (student['first_name'] && student['phone'])
        formDefaults['username'] = `${student['first_name'].trim()}.${student[
          'phone'
        ].trim()}`.toLowerCase();
    return formDefaults;
  };

  getFormFields = () => {
    const { userConfig, programs, formType, student } = this.props;
    return studentFields
      .filter(field => {
        if (formType !== FORM_ACTIONS.CREATE && field.forCreate) {
          return false;
        }
        return true;
      })
      .map(field => {
        if (field.fieldType === FIELD_TYPES.SELECT)
          field.choices = getOptions({ field: field.name, userConfig, programs });
        if (field.name === OPTIONS.STUDENTSTATE) {
          if (formType === FORM_ACTIONS.CREATE) {
            field.choices = field.choices.filter(choice => choice.label !== 'Active');
          }
          field.editable =
            student && student.student_type && student.student_type === 'TEACHER';
        }
        return field;
      });
  };

  static getDerivedStateFromProps(props, state) {
    const { studentList, students, isLast, actions } = props;
    const { studentPost, studentPatch, tab } = state;
    const newStudentPost = store.post(studentList, studentPost.id);
    const newStudentPatch = store.patch(students, studentPatch.id);
    const newState = {};

    if (newStudentPost.resolved) {
      if (newStudentPost.resolved.isCreated) {
        newState.studentPost = newStudentPost;
        newState.studentData = newStudentPost.resolved.data;
        newState.message = 'Student account created successfully';
        if (isLast) newState.type = FORM_ACTIONS.VIEW;
        else newState.tab = tab + 1;
        actions.invalidateStudentList();
      } else if (newStudentPost.resolved.isCreatingFailed) {
        newState.studentPost = newStudentPost;
        newState.errorText = store.getError(newStudentPost.resolved).message;
      }
    }

    if (newStudentPatch.resolved) {
      if (newStudentPatch.resolved.isPatched) {
        newState.studentPatch = newStudentPatch;
        newState.studentData = newStudentPatch.resolved.data;
        newState.type = FORM_ACTIONS.VIEW;
        newState.message = 'Student details edited successfully';
        actions.invalidateStudentList();
      } else if (newStudentPatch.resolved.isPatchingFailed) {
        newState.studentPatch = newStudentPatch;
        newState.errorText = store.getError(newStudentPatch.resolved).message;
      }
      newState.updateStudent = false;
    }
    return newState;
  }

  componentDidUpdate() {
    const {
      tab: formTab,
      formType,
      onNavigate,
      student,
      studentSlots,
      actions,
    } = this.props;
    const {
      tab,
      type,
      studentData,
      message,
      updateStudent,
      studentPatch: { id },
      form,
      formFields,
    } = this.state;

    if (tab !== formTab || type !== formType)
      onNavigate({ tab, type, data: studentData, message });

    if (student && student.id && updateStudent) {
      const studentSlotsGet = store.get(studentSlots, student.id);
      if (studentSlotsGet && studentSlotsGet.isLoaded) {
        const data = studentSlotsGet.data;
        if (data && Array.isArray(data) && data.length > 0) {
          this.setState({
            errorText: 'Expire Student Slots First',
            updateStudent: false,
          });
        } else {
          this.setState(
            {
              updateStudent: false,
            },
            () => {
              const data = {};
              formFields.forEach(({ name, param }) => {
                if (
                  name === 'classEndDate' ||
                  name === 'classStartDate' ||
                  (name === 'churnDate' && student[param] !== form[name])
                ) {
                  data[param] = data[param] ? dateObjToEpoch(form[name]) : null;
                } else if (
                  name === 'isTeacherTraining' &&
                  student[param] !== form[name]
                ) {
                  data[param] =
                    form[name] === true ? STUDENT_TYPE.TEACHER : STUDENT_TYPE.STUDENT;
                } else if (param && student[param] !== form[name])
                  data[param] = form[name];
              });
              actions.editStudent(id, student.id, data);
            },
          );
        }
      }
    }
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const newForm = { ...form };
    const { formType } = this.props || {};

    newForm[key] = value;
    if ((key === 'firstName' || key === 'phone') && formType === FORM_ACTIONS.CREATE) {
      const { firstName = '', phone = '' } = newForm || {};
      const firstNameStripped = firstName.replace(/[ .-]*/g, '');
      const phoneStripped = phone.replace(/[ .-]*/g, '');
      if (firstNameStripped && phoneStripped) {
        newForm.username = `${firstNameStripped}.${phoneStripped}`.toLowerCase();
      } else {
        newForm.username = '';
      }
    }

    const stateChanges = { form: newForm };

    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;

    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const {
      studentPost: { id },
      form,
      formFields,
      studentData,
    } = this.state;
    const { actions } = this.props;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (name === 'classEndDate' || name === 'classStartDate' || name === 'churnDate') {
        data[param] = dateObjToEpoch(form[name]);
      } else if (name === 'isTeacherTraining') {
        data[param] = form[name] === true ? STUDENT_TYPE.TEACHER : STUDENT_TYPE.STUDENT;
      } else if (param) data[param] = form[name];
    });

    let valid = true;
    formFields.every(({ param, label, required, fieldType }) => {
      const isRequired = param && required;
      const val = data[param];
      const isTextNull = !val || (typeof val === 'string' && !val.trim());

      if (isRequired && isTextNull) {
        valid = false;
        this.setState({
          errorText: `${label} is a required field`,
        });
        return false;
      }
      return true;
    });

    if (!valid) {
      return;
    }

    if (studentData && studentData['id'] && studentData['parent_id']) {
      data['student_service_id'] = studentData['id'];
      data['parent_service_id'] = studentData['parent_id'];
    }

    if (data['username'] && data['username'].length > USERNAME_LENGTH) {
      return this.setState({
        errorText: 'Input the username less than 30 characters',
      });
    } else if (data['username'] && data['username'].includes(' ')) {
      return this.setState({
        errorText: 'Username cannot have any spaces',
      });
    }

    actions.createStudent(id, data);
  };

  handlePatchAction = () => {
    const {
      studentPatch: { id },
      form,
      formFields,
    } = this.state;
    const { actions, student } = this.props;
    const data = {};
    data.country_code = student.country_code;

    formFields.forEach(({ name, param }) => {
      if (
        name === 'classEndDate' ||
        name === 'classStartDate' ||
        (name === 'churnDate' && student[param] !== form[name])
      ) {
        data[param] = form[name] ? dateObjToEpoch(form[name]) : null;
      } else if (name === 'isTeacherTraining' && student[param] !== form[name]) {
        data[param] = form[name] === true ? STUDENT_TYPE.TEACHER : STUDENT_TYPE.STUDENT;
      } else if (name === 'password') {
        if (form[name].trim()) {
          data[param] = form[name];
        }
      } else if (param && student[param] !== form[name]) {
        data[param] = form[name];
      }
    });

    if (Object.keys(data).length) {
      if (data.state === 'INACT') {
        this.setState({ updateStudent: true }, () => {
          if (student.id) {
            actions.getStudentSlots(student.id, null, {
              student_id: student.id,
              slot_status: 'live',
            });
          }
        });
      } else {
        actions.editStudent(id, student.id, data);
      }
    }
  };

  onSubmit = event => {
    const {
      studentPost: { current },
      form: { phone, username, password, confirmPassword },
    } = this.state;
    const { formType } = this.props;

    event.preventDefault();

    trackEvent(EVENT_TYPE.EDIT_STUDENT_DETAILS_SUBMIT_CLICKED);
    if (phone && phone === username) {
      this.setState(() => ({ errorText: `Username & Phone number can't be same.` }));
    } else if (password.trim().length >= 1 && password.trim().length < 6) {
      this.setState(() => ({ errorText: 'Passwords must be of atleast 6 Characters' }));
    } else if (password !== confirmPassword)
      this.setState(() => ({ errorText: 'Passwords do not match' }));
    else if (formType === FORM_ACTIONS.CREATE && !(current && current.isCreating))
      this.handlePostAction();
    else if (formType === FORM_ACTIONS.EDIT) this.handlePatchAction();
  };

  nextTab = () => {
    const { tab } = this.state;
    trackEvent(EVENT_TYPE.EDIT_STUDENT_DETAILS_NEXT_CLICKED);
    this.props.onNavigate({ tab: tab + 1 });
  };

  prevTab = () => {
    const { tab } = this.state;
    trackEvent(EVENT_TYPE.EDIT_STUDENT_DETAILS_PREV_CLICKED);
    this.props.onNavigate({ tab: tab - 1 });
  };

  renderFormHeader = () => {
    const { formType } = this.props;

    return (
      <Head>
        <Text size="xlarge" align="center">
          {getHeadText(formType, 'Student')}
        </Text>
      </Head>
    );
  };

  renderFormFields = () => {
    const { formType } = this.props;
    const { formFields, form, studentData } = this.state;
    return (
      <FieldWrapper>
        {formFields.map(field => {
          if (
            PII_HIDDEN_FORM_ACTIONS.includes(formType) &&
            HIDDEN_FIELDS.includes(field.name)
          ) {
            return null;
          }

          if (field.name === 'trial') {
            if (form.studentState === 'TRL') {
              field.disabled = false;
            } else {
              field.disabled = true;
            }
          }

          if (field.name === 'studentState') {
            field.disabled = studentData.is_hybrid;
          }

          if (field.name === 'parentName') {
            if (form.parentName === '') {
              field.editable = true;
            }
          }

          const disabled =
            field.disabled ||
            formType === FORM_ACTIONS.VIEW ||
            (!field.editable && formType === FORM_ACTIONS.EDIT);
          const required = formType === FORM_ACTIONS.CREATE ? field.required : false;
          const placeholder = disabled ? '' : field.label;
          return renderFormField({
            ...field,
            value: form[field.name],
            onChange: this[getInputChangeMethod(field)],
            defaultValue: field.default,
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = () => {
    const { formType, isFirst, isLast } = this.props;
    const { studentData } = this.state;

    return (
      <Footer>
        <ButtonWrapper>
          <NavigationButtons>
            <NavButton
              label={
                formType === FORM_ACTIONS.EDIT ? 'Skip to Previous' : 'Go to Previous'
              }
              disabled={!studentData || isFirst}
              onClick={this.prevTab}
            />
          </NavigationButtons>
          <Button label="SAVE" disabled={formType === FORM_ACTIONS.VIEW} />
          <NavigationButtons>
            <NavButton
              label={formType === FORM_ACTIONS.EDIT ? 'Skip to Next' : 'Go to Next'}
              disabled={!studentData || isLast || formType === FORM_ACTIONS.CREATE}
              onClick={this.nextTab}
            />
          </NavigationButtons>
        </ButtonWrapper>
      </Footer>
    );
  };

  render() {
    const { message } = this.props;
    const { errorText } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter()}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ studentList, students, studentSlots }) => ({ studentList, students, studentSlots }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createStudent,
        editStudent,
        invalidateStudentList,
        getStudentSlots,
      },
      dispatch,
    ),
  }),
)(CreateStudent);
